import type { StateCreator } from 'zustand';
import { directoryClient } from 'shared/domain/directory/directoryClient';
import { documentationClient } from 'shared/domain/documentation/documentationClient';
import {
  DocumentationViewItem,
  isDirectoryItemType,
  isFileItemType,
} from 'store/documentation/model/types';
import { debugLog } from 'shared/logger/debugLog';
import {
  createDocumentationDownloadService,
  DocumentationDownloadConfig,
} from 'services/documentationDownload';
import { DownloadSlice, DownloadConfig } from './types';
import type { DocumentationStoreSlices } from '../types';

export * from './types';

const INITIAL_DOWNLOAD_STATE = {
  isDownloading: false,
  downloadProgress: null,
};

type DownloadSliceCreator = StateCreator<
  DocumentationStoreSlices,
  [],
  [],
  DownloadSlice
>;

const documentationDownloadService = createDocumentationDownloadService({
  getDirectories: (parentId) => directoryClient.getChildren(parentId),
  getDocumentations: (parentId) => documentationClient.getChildren(parentId),
});

function toDocumentationDownloadConfig(
  config: DownloadConfig
): DocumentationDownloadConfig {
  return {
    valueGetters: config.valueGetters,
    filterModel: config.filterModel,
    timezone: config.timezone,
    documentState: config.documentState,
  };
}

export const createDownloadSlice: DownloadSliceCreator = (set, get) => ({
  ...INITIAL_DOWNLOAD_STATE,

  downloadFile: async (
    item: DocumentationViewItem,
    _config: DownloadConfig
  ): Promise<void> => {
    if (!isFileItemType(item.type)) {
      return;
    }

    set({ isDownloading: true });

    try {
      const { files: galleryFiles } = get();
      await documentationDownloadService.downloadFileForItem(
        item,
        galleryFiles
      );
    } catch (error) {
      debugLog('Error downloading file:', error);
      throw error;
    } finally {
      set({ isDownloading: false });
    }
  },

  downloadDirectory: async (
    item: DocumentationViewItem,
    config: DownloadConfig
  ): Promise<void> => {
    if (!isDirectoryItemType(item.type) || !item._id) return;

    set({ isDownloading: true, downloadProgress: 0 });

    try {
      await documentationDownloadService.downloadDirectoryForItem(
        item,
        toDocumentationDownloadConfig(config)
      );
    } catch (error) {
      debugLog('Error downloading directory:', error);
      throw error;
    } finally {
      set({ isDownloading: false, downloadProgress: null });
    }
  },

  downloadSelected: async (
    items: DocumentationViewItem[],
    config: DownloadConfig
  ): Promise<void> => {
    if (items.length === 0) return;

    set({ isDownloading: true, downloadProgress: 0 });

    try {
      await documentationDownloadService.downloadSelectedAsZip(
        items,
        toDocumentationDownloadConfig(config)
      );
    } catch (error) {
      debugLog('Error downloading selected items:', error);
      throw error;
    } finally {
      set({ isDownloading: false, downloadProgress: null });
    }
  },

  setDownloadProgress: (progress: number | null): void => {
    set({ downloadProgress: progress });
  },

  resetDownloadState: (): void => {
    set(INITIAL_DOWNLOAD_STATE);
  },
});
